import { useEffect, useId, useRef, useState } from 'react';
import type { ErpArticle, ErpArticleHolder, ErpMatchReason, ErpNameCheck } from '../../../api';
import { Icon, Input } from '../../primitives';
import { StatusBadge } from '../../ui';
import { articlePriceParts, articleStockValue, matchReasonText, nameCheckText, primaryReason } from './matchingCopy';

/**
 * One ERP article laid out to be compared with the store product beside it
 * (CO-170): its name first, then the facts that identify it (code, barcode),
 * what the ERP says about stock and price for this website, and why it is
 * suggested, as facts. A candidate is chosen with its radio button, so the
 * choice works from the keyboard like any radio group: the arrow keys move
 * between candidates, and 1 to 9 pick one from anywhere on the screen.
 *
 * Without `onSelect` it is a plain display of an article (the one a product is
 * matched to).
 */
export function CandidateCard({
  article,
  reasons,
  nameCheck,
  ambiguous = false,
  shortcut,
  currency,
  pricesIncludeTax,
  selected = false,
  onSelect,
  groupName,
  copyable = false,
  onMove,
  moving = false,
  headingLevel = 4,
}: {
  article: ErpArticle;
  reasons?: ErpMatchReason[];
  nameCheck?: ErpNameCheck;
  /** Another article has the same code or barcode as the product's SKU. */
  ambiguous?: boolean;
  /** The number key that picks it. */
  shortcut?: number;
  currency: string;
  pricesIncludeTax: boolean | null;
  selected?: boolean;
  onSelect?: () => void;
  groupName?: string;
  /** Offers "Copy code", where a match cannot be saved from here. */
  copyable?: boolean;
  /** Takes the article from the one product that carries it (CO-163). */
  onMove?: (holder: ErpArticleHolder) => void;
  moving?: boolean;
  headingLevel?: 3 | 4 | 5;
}) {
  const held = article.held_by ?? [];
  const price = articlePriceParts(article, currency, pricesIncludeTax);
  const nameId = useId();
  const detailId = useId();
  const Heading = `h${headingLevel}` as 'h3' | 'h4' | 'h5';
  const lead = primaryReason(reasons);
  const others = (reasons ?? []).filter((reason) => reason !== lead);
  const pickable = Boolean(onSelect) && held.length === 0;

  const body = (
    <>
      <span className="cand__head">
        {shortcut !== undefined && shortcut <= 9 ? (
          <kbd className="kbd" aria-hidden="true">
            {shortcut}
          </kbd>
        ) : null}
        <Heading id={nameId} className="cand__name">
          {article.name || article.code}
        </Heading>
      </span>
      <span className="cand__meta" id={detailId}>
        <span className="cand__fact">
          <span className="cand__k">ERP code</span>
          <code>{article.code}</code>
        </span>
        <span className="cand__fact">
          <span className="cand__k">Barcode</span>
          {article.barcode ? <code>{article.barcode}</code> : <span className="muted">None</span>}
        </span>
        <span className="cand__fact tabular-nums">
          <span className="cand__k">Stock</span>
          {articleStockValue(article)}
        </span>
        <span className="cand__fact tabular-nums">
          <span className="cand__k">Price</span>
          {price.amount}
          {price.tax ? <span className="cand__sub"> {price.tax}</span> : null}
        </span>
      </span>
      {lead || nameCheck ? (
        <span className="cand__evidence" data-testid="match-evidence">
          {lead ? <StatusBadge kind="matchEvidence" value={lead} title={matchReasonText[lead]} /> : null}
          {others.map((reason) => (
            <StatusBadge key={reason} kind="matchEvidence" value={reason} title={matchReasonText[reason]} />
          ))}
          {nameCheck && nameCheck !== 'unknown' ? <StatusBadge kind="matchNames" value={nameCheck} /> : null}
          {ambiguous ? <span className="badge badge--warning">Another article has the same code</span> : null}
        </span>
      ) : null}
      {reasons && reasons.length > 0 ? (
        <span className="visually-hidden">{reasons.map((reason) => matchReasonText[reason]).join(' ')}</span>
      ) : null}
      {nameCheck === 'differ' ? <span className="cand__why">{nameCheckText.differ}</span> : null}
      {article.sellable === false ? <span className="cand__why">The ERP marks this article as not for sale.</span> : null}
    </>
  );

  return (
    <article className={`cand${selected ? ' cand--selected' : ''}${held.length > 0 ? ' cand--held' : ''}${nameCheck === 'differ' ? ' cand--conflict' : ''}`} data-testid={`match-article-${article.code}`}>
      {onSelect ? (
        <label className="cand__pick">
          <Input
            type="radio"
            name={groupName}
            className="cand__radio"
            checked={selected}
            disabled={!pickable}
            onChange={onSelect}
            aria-labelledby={nameId}
            aria-describedby={detailId}
            data-candidate-code={article.code}
          />
          <span className="cand__mark" aria-hidden="true">
            {selected ? <Icon name="check" size={14} /> : null}
          </span>
          <span className="cand__body">{body}</span>
        </label>
      ) : (
        <div className="cand__pick cand__pick--static">
          <span className="cand__body">{body}</span>
        </div>
      )}
      {held.length > 0 ? (
        <div className="cand__held" data-testid="match-article-held">
          <p>
            Already used by {held.map((product) => `${product.name || product.sku} (${product.sku})`).join(', ')}.
            {onMove && held.length > 1 ? ' To use it here, first remove the match from all but one of them.' : ''}
          </p>
          {onMove && held.length === 1 ? (
            <button type="button" className="button button--secondary button--small" disabled={moving} onClick={() => onMove(held[0]!)}>
              {moving ? 'Asking the store' : 'Move the match here'}
            </button>
          ) : null}
        </div>
      ) : null}
      {copyable ? <CopyCode code={article.code} /> : null}
    </article>
  );
}

/** Copies an article's code, where the match has to be entered in the store by hand. */
function CopyCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  async function copy() {
    let done = false;
    try {
      await navigator.clipboard.writeText(code);
      done = true;
    } catch {
      // The clipboard is unavailable (an insecure page, a denied permission):
      // the code is shown selectable, so it can still be copied by hand.
    }
    setCopied(done);
    window.clearTimeout(timer.current);
    if (done) timer.current = window.setTimeout(() => setCopied(false), 2500);
  }
  return (
    <div className="cand__actions">
      <button type="button" className="button button--secondary button--small" onClick={() => void copy()}>
        Copy code
      </button>
      <span className="match-article__copied" role="status">
        {copied ? 'Code copied' : ''}
      </span>
    </div>
  );
}
